import { useEffect, useRef, useState } from "react";

// Space kept above a target for the fixed header.
const TOP_CLEARANCE = 96;
// A target that moves further than this on its own (not from our scrolling or
// the user's) counts as a layout shift and triggers a re-plan.
const SHIFT_PX = 40;

const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export const prefersReducedMotion = () => {
  try {
    return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  } catch {
    return false;
  }
};

const boxOf = (el) => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

const blend = (from, to, t) => ({
  top: from.top + (to.top - from.top) * t,
  left: from.left + (to.left - from.left) * t,
  width: from.width + (to.width - from.width) * t,
  height: from.height + (to.height - from.height) * t,
});

const same = (a, b) =>
  Boolean(a && b) &&
  Math.abs(a.top - b.top) < 0.5 &&
  Math.abs(a.left - b.left) < 0.5 &&
  Math.abs(a.width - b.width) < 0.5 &&
  Math.abs(a.height - b.height) < 0.5;

const pinnedToViewport = (el) => {
  for (let node = el; node && node !== document.body; node = node.parentElement) {
    if (getComputedStyle(node).position === "fixed") return true;
  }
  return false;
};

const setScroll = (top) => {
  try {
    window.scrollTo({ top, behavior: "instant" });
  } catch {
    window.scrollTo(0, top);
  }
};

const scrollerOf = (el) => {
  for (let node = el.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight + 1) return node;
  }
  return null;
};

const innerTopFor = (scroller, box) => {
  const frame = scroller.getBoundingClientRect();
  if (box.top >= frame.top + 8 && box.top + box.height <= frame.bottom - 8) return scroller.scrollTop;
  const offset = box.top - frame.top + scroller.scrollTop;
  const want =
    box.height > frame.height * 0.8 ? offset - 8 : offset + box.height / 2 - frame.height / 2;
  return Math.min(Math.max(0, want), scroller.scrollHeight - scroller.clientHeight);
};

export const scrollTopFor = (box) => {
  const view = window.innerHeight;
  const page = document.scrollingElement || document.documentElement;
  const max = Math.max(0, (page?.scrollHeight || 0) - view);
  if (box.top >= TOP_CLEARANCE - 8 && box.top + box.height <= view - 16) return window.scrollY;
  const want =
    box.height > view * 0.55
      ? window.scrollY + box.top - TOP_CLEARANCE
      : window.scrollY + box.top + box.height / 2 - view / 2;
  return Math.min(Math.max(0, want), max);
};

export const useGlide = (target, resetKey) => {
  const [rect, setRect] = useState(null);
  const [moving, setMoving] = useState(false);
  const shownRef = useRef(null);
  const keyRef = useRef(resetKey);

  useEffect(() => {
    if (keyRef.current !== resetKey) {
      keyRef.current = resetKey;
      shownRef.current = null;
    }
    if (!target) {
      shownRef.current = null;
      setRect(null);
      setMoving(false);
      return undefined;
    }

    const still = prefersReducedMotion();
    let frame = null;
    let glide = null;
    let last = null;
    let held = null; // inner scroll panel found by the latest plan()
    let settles = 0;

    const show = (next) => {
      if (same(shownRef.current, next)) return;
      shownRef.current = next;
      setRect(next);
    };

    const remember = () => {
      last = { box: boxOf(target), y: window.scrollY, scroller: held, inner: held ? held.scrollTop : 0 };
    };

    const step = () => {
      frame = null;
      if (!glide) return;
      if (!target.isConnected) {
        // The target was replaced or removed mid-glide: stop, or `follow` would
        // bail on the stale glide forever and the tip would stay faded.
        glide = null;
        setMoving(false);
        return;
      }
      const t = Math.min(1, (performance.now() - glide.startedAt) / glide.duration);
      const e = ease(t);
      if (glide.toY !== glide.fromY) setScroll(glide.fromY + (glide.toY - glide.fromY) * e);
      if (glide.scroller && glide.toInner !== glide.fromInner) {
        glide.scroller.scrollTop = glide.fromInner + (glide.toInner - glide.fromInner) * e;
      }
      const live = boxOf(target);
      const innerLeft = glide.scroller ? glide.scroller.scrollTop - glide.toInner : 0;
      const landing = glide.pinned
        ? { ...live, top: live.top + innerLeft }
        : { ...live, top: live.top + innerLeft + window.scrollY - glide.toY };
      show(glide.from ? blend(glide.from, landing, e) : live);
      if (t < 1) {
        frame = requestAnimationFrame(step);
        return;
      }
      glide = null;
      const next = plan();
      if (next.travel > 8 && settles < 2) {
        settles += 1;
        startGlide(next.toY, Math.min(520, 280 + next.travel * 0.2), next.scroller, next.toInner);
        return;
      }
      setMoving(false);
      remember();
    };

    const startGlide = (toY, duration, scroller = null, toInner = 0) => {
      if (frame) cancelAnimationFrame(frame);
      glide = {
        from: shownRef.current,
        fromY: window.scrollY,
        toY,
        scroller,
        fromInner: scroller ? scroller.scrollTop : 0,
        toInner: scroller ? toInner : 0,
        duration,
        pinned: pinnedToViewport(target),
        startedAt: performance.now(),
      };
      setMoving(true);
      frame = requestAnimationFrame(step);
    };

    const plan = () => {
      const scroller = scrollerOf(target);
      const box = boxOf(target);
      const toInner = scroller ? innerTopFor(scroller, box) : 0;
      const shiftInner = scroller ? toInner - scroller.scrollTop : 0;
      held = scroller;
      // Scrolling the page never moves a fixed target, so don't try.
      const toY = pinnedToViewport(target) ? window.scrollY : scrollTopFor({ ...box, top: box.top - shiftInner });
      return { scroller, toInner, toY, travel: Math.abs(toY - window.scrollY) + Math.abs(shiftInner) };
    };
    const { scroller, toInner, toY, travel } = plan();
    if (still) {
      if (scroller) scroller.scrollTop = toInner;
      if (toY !== window.scrollY) setScroll(toY);
      show(boxOf(target));
      setMoving(false);
      remember();
    } else if (shownRef.current || travel > 0) {
      startGlide(toY, Math.min(640, 360 + travel * 0.22), scroller, toInner);
    } else {
      show(boxOf(target));
      setMoving(false);
      remember();
    }

    const follow = () => {
      if (glide || !target.isConnected) return;
      const live = boxOf(target);
      if (last && !still && shownRef.current) {
        const dTop = live.top - last.box.top;
        const dScroll = window.scrollY - last.y;
        // Page scroll shifts the box by -dScroll, inner-panel scroll by -dInner;
        // neither is a layout shift, so judge the movement with both removed.
        const dInner = last.scroller ? last.scroller.scrollTop - last.inner : 0;
        const shifted =
          Math.min(Math.abs(dTop), Math.abs(dTop + dScroll + dInner)) > SHIFT_PX ||
          Math.abs(live.height - last.box.height) > SHIFT_PX;
        if (shifted) {
          const next = plan();
          startGlide(next.toY, next.travel ? Math.min(560, 300 + next.travel * 0.2) : 260, next.scroller, next.toInner);
          return;
        }
      }
      show(live);
      remember();
    };

    const rechecks = [450, 900, 1500].map((ms) => setTimeout(follow, ms));
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(follow);
      observer.observe(target);
      observer.observe(document.body);
    }

    return () => {
      if (frame) cancelAnimationFrame(frame);
      rechecks.forEach(clearTimeout);
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
      observer?.disconnect();
    };
  }, [target, resetKey]);

  return { rect, moving };
};
